import React from 'react';
import { PLATFORMS, INDUSTRIES, STATS, SERVICES } from '@/data/mock';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Eye, Boxes, ShieldCheck, Activity, Layers, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const platformIcons = {
  'industrial-ai': Cpu,
  'visopercept': Eye,
  'digital-twin': Boxes,
};

export function StatsStrip() {
  return (
    <section className="border-y border-white/5 bg-[#0a0d12]">
      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {STATS.map((s) => (
          <div key={s.label} className="">
            <div className="font-display text-3xl md:text-4xl font-bold text-cyan-300">{s.value}</div>
            <div className="text-xs md:text-sm text-slate-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PlatformSection() {
  return (
    <section id="platform" className="relative py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">01 · The Platform</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold max-w-3xl leading-tight">Observe. Understand. Automate physical operations.</h2>
            <p className="text-slate-400 mt-4 max-w-2xl">A unified Physical AI stack — perception, traceability and a live digital twin — built for the industrial edge.</p>
          </div>
          <Link to="/platform"><Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">Explore the platform <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {PLATFORMS.map((p) => {
            const Icon = platformIcons[p.id] || Cpu;
            return (
              <div key={p.id} className="card-glow group relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-7 transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-lg">{p.name}</div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500">{p.tag}</div>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-slate-400 mt-4 text-sm leading-relaxed">{p.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.features.map((f) => (
                    <span key={f} className="px-2.5 py-1 text-xs rounded-md bg-white/5 border border-white/10 text-slate-300">{f}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function IndustriesSection() {
  return (
    <section id="industries" className="relative py-24 border-t border-white/5 bg-[#0a0d12]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">02 · Industries</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold max-w-3xl leading-tight">Built for the hardest physical environments.</h2>
            <p className="text-slate-400 mt-4 max-w-2xl">Pre-trained models, integrations, and playbooks for the industries that move the world.</p>
          </div>
          <Link to="/industries"><Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">All industries <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((ind) => (
            <div key={ind.id} className="group card-glow relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5] cursor-pointer">
              <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07090c] via-[#07090c]/50 to-transparent" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                <div className="text-[11px] font-mono uppercase tracking-widest text-cyan-300">{ind.tag}</div>
                <div className="font-display text-2xl font-bold mt-1">{ind.name}</div>
                <div className="mt-3 space-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-h-0 group-hover:max-h-40 overflow-hidden">
                  {ind.outcomes.map((o) => (
                    <div key={o} className="text-xs text-slate-200 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-cyan-300" />{o}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ContextSection() {
  const nodes = [
    { icon: Layers, title: 'Video streams', sub: 'CCTV · PTZ · line-scan' },
    { icon: Eye, title: 'Perception', sub: 'VisoPercept models' },
    { icon: Boxes, title: 'Digital Twin', sub: 'Live 2D / 3D state' },
    { icon: ShieldCheck, title: 'Policies', sub: 'SOPs · safety · alerts' },
    { icon: Activity, title: 'Actions', sub: 'TOS · WMS · AODB' },
  ];
  return (
    <section className="relative py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">The Context Graph</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">The missing operational context for Physical AI.</h2>
            <p className="text-slate-400 mt-4">Visotonics fuses video, sensors, and business systems into a live context graph — so agents and people reason, decide and act on ground truth.</p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {['Sub-100ms edge', 'Sovereign / air-gapped', 'ISO 27001 by design', 'On-prem or cloud'].map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300">{t}</span>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6 viso-grid overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {nodes.map((n) => (
                  <div key={n.title} className="rounded-xl border border-white/10 bg-black/30 p-4 hover:border-cyan-400/40 transition-colors">
                    <n.icon className="w-4 h-4 text-cyan-300" />
                    <div className="font-display font-semibold mt-2 text-sm">{n.title}</div>
                    <div className="text-xs text-slate-400">{n.sub}</div>
                  </div>
                ))}
              </div>
              <div className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-cyan-400/10 blur-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ServicesSection() {
  return (
    <section className="relative py-24 border-t border-white/5 bg-[#0a0d12]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">03 · Services</div>
        <h2 className="font-display text-3xl md:text-5xl font-bold max-w-3xl leading-tight">Expertise that delivers. Outcomes that compound.</h2>
        <p className="text-slate-400 mt-4 max-w-2xl">Eight stages, one outcome — autonomous physical operations. From advisory to enterprise-wide transformation.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
          {SERVICES.map((s) => (
            <div key={s.stage} className="rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-cyan-400/30 hover:bg-white/[0.04] transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-slate-500">Stage {s.stage}</span>
                <Zap className="w-3.5 h-3.5 text-cyan-300" />
              </div>
              <div className="font-display font-semibold text-lg mt-2">{s.title}</div>
              <ul className="mt-3 space-y-1.5">
                {s.items.map((it) => (
                  <li key={it} className="text-xs text-slate-400 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-slate-500" />{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection() {
  return (
    <section className="relative py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 md:p-16">
          <div className="absolute inset-0 viso-grid opacity-60" />
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="relative">
            <h3 className="font-display text-3xl md:text-5xl font-bold max-w-3xl">Make Physical AI work in your operations.</h3>
            <p className="text-slate-400 mt-4 max-w-2xl">Join 85+ ports, DCs and airfields running on Visotonics. Book a live demo or start a pilot.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact"><Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">Book a demo <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to="/partners"><Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">Become a partner</Button></Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
